import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input — Shadcn input primitive with WCAG 2.1 Phase 2 hardening.
 *
 *  • If `required` is set, also exposes `aria-required="true"` (browsers
 *    don't always reflect the `required` attribute as ARIA).
 *  • If `error` is truthy, sets `aria-invalid="true"`. Consumers should pair
 *    this with `aria-describedby` pointing at a <FormError id>.
 *  • If the input is missing both `aria-label`, `aria-labelledby`, and an
 *    associated `<label htmlFor={id}>`, we leave behaviour untouched —
 *    consumers are responsible for visible labelling.
 */
const Input = React.forwardRef(({ className, type, required, error, "aria-invalid": ariaInvalid, ...props }, ref) => {
  const isInvalid = ariaInvalid !== undefined ? ariaInvalid : !!error;
  return (
    <input
      type={type}
      aria-required={required ? "true" : undefined}
      aria-invalid={isInvalid || undefined}
      required={required}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        // Visual error state
        isInvalid && "border-red-500 focus-visible:ring-red-500",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
